import { c as _c } from "react/compiler-runtime";
// Third Party
import * as React from 'react';
import cx from 'classnames';

// Utils
import dayjs from '@utils/dayjs-setup';
import { firstBookableCheckInDate, isDatedSearchTooSoon } from '@utils/booking-lead-time';
import { scrollToTop } from '@utils/scroll-to-top';
import { useCheckInTooSoonReport, useFavoritedListingIds } from '@hooks';
import { useSearch } from '@contexts/search-context';
import { routes } from '@routes';
import { FeatureKey } from '@gql';

// UI
import { ListingCard } from '@components/listing-card';
import { Typography } from '@components/typography';
import { Link } from '@components/link';
import { Icon, IconNames } from '@components/icon';
import { Spinner, SpinnerSize } from '@components/spinner';
import { Button } from '@components/button';
import { CheckInTooSoonNotice } from '@components/check-in-too-soon-notice';
import { ButtonIcon } from '@components/button-icon';
import { Dropdown } from '@components/dropdown';
import { Input } from '@components/form/input';
import { SearchDateRangePicker, type FocusedInputShape, type SearchDateRange } from '@components/search-date-range-picker';

// TODO - move to components
import { FeaturesModal } from '@app/(search)/_components/search/search-map/features-modal';
import { concatenateAndReplace } from '@utils/test-helpers';
type Props = {
  accountSlug: string;
};
export const AccountShowList: React.FC<Props> = t0 => {
  const $ = _c(172);
  const {
    accountSlug
  } = t0;
  const {
    checkInDate,
    checkOutDate,
    listingsLoading,
    listingsQueryResults,
    minNumberOfBathrooms,
    minNumberOfRooms,
    minNumberOfBeds,
    propertyTypes,
    resetPagination,
    setCheckInDate,
    setCheckOutDate,
    setMinNumberOfBathrooms,
    setMinNumberOfRooms,
    setMinNumberOfBeds,
    setPropertyTypes,
    activateFeaturesModal,
    activeFilters,
    setActiveFilters,
    nextPage,
    prevPage
  } = useSearch();
  const {
    isFavorited,
    setFavorited
  } = useFavoritedListingIds();
  const hasOnlyPetFriendlyFilter = activeFilters.length === 1 && activeFilters[0].key === FeatureKey.PetFriendly;
  const hasFiltersApplied = activeFilters.length > 0 && !hasOnlyPetFriendlyFilter || minNumberOfBathrooms > 0 || minNumberOfBeds > 0 || minNumberOfRooms || propertyTypes.length > 0;
  let t1;
  if ($[0] !== setCheckInDate || $[1] !== setCheckOutDate) {
    t1 = () => {
      setCheckInDate(null);
      setCheckOutDate(null);
    };
    $[0] = setCheckInDate;
    $[1] = setCheckOutDate;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const clearDates = t1;
  const hasNoResults = !listingsLoading && listingsQueryResults?.listingsSearch.__typename === "ListingSearchCursorCollection" && listingsQueryResults.listingsSearch.entries.length === 0;
  let t2;
  if ($[3] !== checkInDate || $[4] !== checkOutDate || $[5] !== hasNoResults) {
    t2 = hasNoResults && isDatedSearchTooSoon(checkInDate, checkOutDate) ? firstBookableCheckInDate() : null;
    $[3] = checkInDate;
    $[4] = checkOutDate;
    $[5] = hasNoResults;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const firstBookableCheckIn = t2;
  let t3;
  if ($[7] !== checkInDate || $[8] !== checkOutDate || $[9] !== firstBookableCheckIn) {
    t3 = firstBookableCheckIn && checkInDate ? {
      checkInDate,
      checkOutDate,
      firstBookableCheckInDate: firstBookableCheckIn,
      surface: "account"
    } : null;
    $[7] = checkInDate;
    $[8] = checkOutDate;
    $[9] = firstBookableCheckIn;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useCheckInTooSoonReport(t3);
  let t4;
  if ($[11] !== setActiveFilters || $[12] !== setMinNumberOfBathrooms || $[13] !== setMinNumberOfBeds || $[14] !== setMinNumberOfRooms || $[15] !== setPropertyTypes) {
    t4 = () => {
      setPropertyTypes([]);
      setActiveFilters([]);
      setMinNumberOfRooms(0);
      setMinNumberOfBathrooms(0);
      setMinNumberOfBeds(0);
    };
    $[11] = setActiveFilters;
    $[12] = setMinNumberOfBathrooms;
    $[13] = setMinNumberOfBeds;
    $[14] = setMinNumberOfRooms;
    $[15] = setPropertyTypes;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const resetFilters = t4;
  const [dateRangePickerInputElement, setDateRangePickerInputElement] = React.useState(null);
  const [dateRangePickerFocusedInput, setDateRangePickerFocusedInput] = React.useState(null);
  let t5;
  if ($[17] !== setCheckInDate || $[18] !== setCheckOutDate) {
    t5 = range => {
      setCheckInDate(range.checkInDate);
      setCheckOutDate(range.checkOutDate);
    };
    $[17] = setCheckInDate;
    $[18] = setCheckOutDate;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleDatesChange = t5;
  const [roomInputElement, setRoomInputElement] = React.useState(null);
  const [roomFocusedInput, setRoomFocusedInput] = React.useState(false);
  if (listingsQueryResults?.listingsSearch.__typename === "ValidationErrorCollection") {
    let t6;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div>Something went wrong</div>;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    return t6;
  }
  let t6;
  if ($[21] !== resetPagination || $[22] !== setActiveFilters || $[23] !== setMinNumberOfBathrooms || $[24] !== setMinNumberOfBeds || $[25] !== setMinNumberOfRooms || $[26] !== setPropertyTypes) {
    t6 = <FeaturesModal viewAllProperties={(propertyTypes_0, minNumberOfRooms_0, minNumberOfBathrooms_0, _minPrice, _maxPrice, minNumberOfBeds_0, filters) => {
      setPropertyTypes(propertyTypes_0);
      setMinNumberOfRooms(minNumberOfRooms_0);
      setMinNumberOfBathrooms(minNumberOfBathrooms_0);
      setMinNumberOfBeds(minNumberOfBeds_0);
      setActiveFilters(filters);
      resetPagination();
    }} />;
    $[21] = resetPagination;
    $[22] = setActiveFilters;
    $[23] = setMinNumberOfBathrooms;
    $[24] = setMinNumberOfBeds;
    $[25] = setMinNumberOfRooms;
    $[26] = setPropertyTypes;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== checkInDate) {
    t7 = checkInDate ? dayjs(checkInDate).format("MMM DD") : undefined;
    $[28] = checkInDate;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setDateRangePickerFocusedInput("startDate");
    };
    t9 = <Icon name={IconNames.CALENDAR_RANGE} className="text-primary-main" />;
    $[30] = t8;
    $[31] = t9;
  } else {
    t8 = $[30];
    t9 = $[31];
  }
  let t10;
  if ($[32] !== checkInDate || $[33] !== setCheckInDate) {
    t10 = checkInDate ? <ButtonIcon variant="text" color="gray" size="sm" icon={IconNames.CIRCLE_X} iconSize="lg" ariaDescribedby="clear-check-in-date-input" ariaLabel="Clear check-in date" onClick={() => {
      setCheckInDate(null);
      setDateRangePickerFocusedInput("startDate");
    }} className="ps-0 pe-0" /> : <span className="invisible ps-0 pe-0 inline-flex items-center justify-center" aria-hidden="true" role="presentation"><ButtonIcon variant="text" size="sm" icon={IconNames.CIRCLE_X} iconSize="lg" ariaDescribedby="clear-check-in-date-placeholder" ariaLabel="Clear check-in date" className="ps-0 pe-0" tabIndex={-1} disabled={true} /></span>;
    $[32] = checkInDate;
    $[33] = setCheckInDate;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t7) {
    t11 = <div className="min-w-[125px]"><Input label="Check-in" name="checkInDate" data-testid="@account/check-in-date" value={t7} onFocus={t8} leftContent={t9} rightContent={t10} autoComplete="off" hasCaret={false} /></div>;
    $[35] = t10;
    $[36] = t7;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== checkOutDate) {
    t12 = checkOutDate ? dayjs(checkOutDate).format("MMM DD") : undefined;
    $[38] = checkOutDate;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== checkInDate) {
    t13 = () => {
      if (!checkInDate) {
        setDateRangePickerFocusedInput("startDate");
      } else {
        setDateRangePickerFocusedInput("endDate");
      }
    };
    $[40] = checkInDate;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Icon name={IconNames.CALENDAR_RANGE} className="text-primary-main" />;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== checkOutDate || $[44] !== setCheckOutDate) {
    t15 = checkOutDate ? <ButtonIcon variant="text" color="gray" size="sm" icon={IconNames.CIRCLE_X} iconSize="lg" ariaDescribedby="clear-check-out-date-input" ariaLabel="Clear check-out date" onClick={() => {
      setCheckOutDate(null);
      setDateRangePickerFocusedInput("endDate");
    }} className="ps-0 pe-0" /> : <span className="invisible ps-0 pe-0 inline-flex items-center justify-center" aria-hidden="true" role="presentation"><ButtonIcon variant="text" size="sm" icon={IconNames.CIRCLE_X} iconSize="lg" ariaDescribedby="clear-check-out-date-placeholder" ariaLabel="Clear check-out date" className="ps-0 pe-0" tabIndex={-1} disabled={true} /></span>;
    $[43] = checkOutDate;
    $[44] = setCheckOutDate;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== t12 || $[47] !== t13 || $[48] !== t15) {
    t16 = <div className="min-w-[125px]"><Input label="Check-out" name="checkOutDate" data-testid="@account/check-out-date" value={t12} onFocus={t13} leftContent={t14} rightContent={t15} autoComplete="off" hasCaret={false} /></div>;
    $[46] = t12;
    $[47] = t13;
    $[48] = t15;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t11 || $[51] !== t16) {
    t17 = <div className="flex whitespace-nowrap space-x-2" ref={setDateRangePickerInputElement}>{t11}{t16}</div>;
    $[50] = t11;
    $[51] = t16;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  const t18 = !!dateRangePickerFocusedInput;
  let t19;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      setDateRangePickerFocusedInput(null);
    };
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== checkInDate || $[55] !== checkOutDate || $[56] !== dateRangePickerFocusedInput || $[57] !== handleDatesChange) {
    t20 = <div className="px-2"><SearchDateRangePicker focusedInput={dateRangePickerFocusedInput} onFocusChange={setDateRangePickerFocusedInput} checkInDate={checkInDate} checkOutDate={checkOutDate} onDatesChange={handleDatesChange} /></div>;
    $[54] = checkInDate;
    $[55] = checkOutDate;
    $[56] = dateRangePickerFocusedInput;
    $[57] = handleDatesChange;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== setCheckInDate || $[60] !== setCheckOutDate) {
    t21 = <div className="flex w-full justify-end pb-2 pr-2"><Button variant="text" className="bg-white" onClick={() => {
        setCheckInDate(null);
        setCheckOutDate(null);
        setDateRangePickerFocusedInput("startDate");
      }}>Clear</Button></div>;
    $[59] = setCheckInDate;
    $[60] = setCheckOutDate;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== dateRangePickerInputElement || $[63] !== t18 || $[64] !== t20 || $[65] !== t21) {
    t22 = <div className="absolute z-50 top-[7.15rem] -left-2"><Dropdown dropdownWidth="635px" reference={dateRangePickerInputElement} isOpen={t18} onClickAway={t19} data-id="date-range-picker-dropdown">{t20}{t21}</Dropdown></div>;
    $[62] = dateRangePickerInputElement;
    $[63] = t18;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  let t24;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Icon name={IconNames.FILTER} size="sm" />;
    t24 = <span>Filters</span>;
    $[67] = t23;
    $[68] = t24;
  } else {
    t23 = $[67];
    t24 = $[68];
  }
  let t25;
  if ($[69] !== activateFeaturesModal) {
    t25 = <Button data-testid="@filters/filters-btn" onClick={activateFeaturesModal} variant="outlined" size="lg" className="focus:outline-none">{t23}{t24}</Button>;
    $[69] = activateFeaturesModal;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== roomFocusedInput) {
    t26 = () => setRoomFocusedInput(!roomFocusedInput);
    $[71] = roomFocusedInput;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  const t27 = roomFocusedInput ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN;
  let t28;
  if ($[73] !== t27) {
    t28 = <Icon name={t27} size="sm" />;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span>Rooms</span>;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== t26 || $[77] !== t28) {
    t30 = <Button data-testid="@filters/rooms-dropdown-btn" onClick={t26} variant="outlined" size="lg">{t28}{t29}</Button>;
    $[76] = t26;
    $[77] = t28;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => {
      setRoomFocusedInput(false);
    };
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Typography variant="subtitle2" component="p">Bedrooms</Typography>;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== resetPagination || $[82] !== setMinNumberOfRooms) {
    t33 = <ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-bedroom" ariaLabel="Remove bedroom" variant="text" className="text-gray-main border-gray-main" onClick={() => {
      setMinNumberOfRooms(_temp);
      resetPagination();
    }} />;
    $[81] = resetPagination;
    $[82] = setMinNumberOfRooms;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== minNumberOfRooms) {
    t34 = <input type="number" aria-label="Number of bedrooms" className="w-6 text-center" value={minNumberOfRooms} />;
    $[84] = minNumberOfRooms;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== resetPagination || $[87] !== setMinNumberOfRooms) {
    t35 = <ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-bedroom" ariaLabel="Add bedroom" variant="text" className="text-gray-main border-gray-main" onClick={() => {
      setMinNumberOfRooms(_temp2);
      resetPagination();
    }} />;
    $[86] = resetPagination;
    $[87] = setMinNumberOfRooms;
    $[88] = t35;
  } else {
    t35 = $[88];
  }
  let t36;
  if ($[89] !== t33 || $[90] !== t34 || $[91] !== t35) {
    t36 = <div className="flex justify-between items-center pb-2">{t32}<div className="flex space-x-2 justify-between">{t33}{t34}{t35}</div></div>;
    $[89] = t33;
    $[90] = t34;
    $[91] = t35;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Typography variant="subtitle2" component="p">Bathrooms</Typography>;
    $[93] = t37;
  } else {
    t37 = $[93];
  }
  let t38;
  if ($[94] !== resetPagination || $[95] !== setMinNumberOfBathrooms) {
    t38 = <ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-bathroom" ariaLabel="Remove bathroom" variant="text" className="text-gray-main border-gray-main" onClick={() => {
      setMinNumberOfBathrooms(_temp3);
      resetPagination();
    }} />;
    $[94] = resetPagination;
    $[95] = setMinNumberOfBathrooms;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  let t39;
  if ($[97] !== minNumberOfBathrooms) {
    t39 = <input type="number" aria-label="Number of bathrooms" className="w-6 text-center" value={minNumberOfBathrooms} />;
    $[97] = minNumberOfBathrooms;
    $[98] = t39;
  } else {
    t39 = $[98];
  }
  let t40;
  if ($[99] !== resetPagination || $[100] !== setMinNumberOfBathrooms) {
    t40 = <ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-bathroom" ariaLabel="Add bathroom" variant="text" className="text-gray-main border-gray-main" onClick={() => {
      setMinNumberOfBathrooms(_temp4);
      resetPagination();
    }} />;
    $[99] = resetPagination;
    $[100] = setMinNumberOfBathrooms;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  let t41;
  if ($[102] !== t38 || $[103] !== t39 || $[104] !== t40) {
    t41 = <div className="flex justify-between items-center pt-2">{t37}<div className="flex space-x-2 justify-between">{t38}{t39}{t40}</div></div>;
    $[102] = t38;
    $[103] = t39;
    $[104] = t40;
    $[105] = t41;
  } else {
    t41 = $[105];
  }
  let t42;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <Typography variant="subtitle2" component="p">Beds</Typography>;
    $[106] = t42;
  } else {
    t42 = $[106];
  }
  let t43;
  if ($[107] !== resetPagination || $[108] !== setMinNumberOfBeds) {
    t43 = <ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-bed" ariaLabel="Remove bed" variant="text" className="text-gray-main border-gray-main" onClick={() => {
      setMinNumberOfBeds(_temp5);
      resetPagination();
    }} />;
    $[107] = resetPagination;
    $[108] = setMinNumberOfBeds;
    $[109] = t43;
  } else {
    t43 = $[109];
  }
  let t44;
  if ($[110] !== minNumberOfBeds) {
    t44 = <input type="number" aria-label="Number of beds" className="w-6 text-center" value={minNumberOfBeds} />;
    $[110] = minNumberOfBeds;
    $[111] = t44;
  } else {
    t44 = $[111];
  }
  let t45;
  if ($[112] !== resetPagination || $[113] !== setMinNumberOfBeds) {
    t45 = <ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-bed" ariaLabel="Add bed" variant="text" className="text-gray-main border-gray-main" onClick={() => {
      setMinNumberOfBeds(_temp6);
      resetPagination();
    }} />;
    $[112] = resetPagination;
    $[113] = setMinNumberOfBeds;
    $[114] = t45;
  } else {
    t45 = $[114];
  }
  let t46;
  if ($[115] !== t43 || $[116] !== t44 || $[117] !== t45) {
    t46 = <div className="flex justify-between items-center pt-2">{t42}<div className="flex space-x-2 justify-between">{t43}{t44}{t45}</div></div>;
    $[115] = t43;
    $[116] = t44;
    $[117] = t45;
    $[118] = t46;
  } else {
    t46 = $[118];
  }
  let t47;
  if ($[119] !== roomFocusedInput || $[120] !== roomInputElement || $[121] !== t36 || $[122] !== t41 || $[123] !== t46) {
    t47 = <div className="absolute z-50 top-[7.15rem] align-center"><Dropdown dropdownWidth="275px" reference={roomInputElement} isOpen={roomFocusedInput} onClickAway={t31} data-id="guest-count-picker-dropdown" className="p-4 divide-y divide-gray-light inset-0 items-start bg-white">{t36}{t41}{t46}</Dropdown></div>;
    $[119] = roomFocusedInput;
    $[120] = roomInputElement;
    $[121] = t36;
    $[122] = t41;
    $[123] = t46;
    $[124] = t47;
  } else {
    t47 = $[124];
  }
  let t48;
  if ($[125] !== t25 || $[126] !== t30 || $[127] !== t47) {
    t48 = <div className="flex space-x-2" ref={setRoomInputElement}>{t25}{t30}{t47}</div>;
    $[125] = t25;
    $[126] = t30;
    $[127] = t47;
    $[128] = t48;
  } else {
    t48 = $[128];
  }
  let t49;
  if ($[129] !== t17 || $[130] !== t22 || $[131] !== t48) {
    t49 = <div className="flex flex-row justify-between align-middle items-center mt-8 space-x-4"><div className="hidden lg:flex flex-row w-full items-center justify-center align-middle whitespace-nowrap space-x-2 py-4">{t17}{t22}{t48}</div></div>;
    $[129] = t17;
    $[130] = t22;
    $[131] = t48;
    $[132] = t49;
  } else {
    t49 = $[132];
  }
  let t50;
  let t51;
  if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <Icon name={IconNames.FILTER} size="sm" />;
    t51 = <span>Filters</span>;
    $[133] = t50;
    $[134] = t51;
  } else {
    t50 = $[133];
    t51 = $[134];
  }
  let t52;
  if ($[135] !== activateFeaturesModal) {
    t52 = <div className="lg:hidden flex items-end whitespace-nowrap pr-4 text-primary-main"><Button onClick={activateFeaturesModal} variant="outlined" size="sm" className="focus:outline-none">{t50}{t51}</Button></div>;
    $[135] = activateFeaturesModal;
    $[136] = t52;
  } else {
    t52 = $[136];
  }
  let t53;
  if ($[137] !== accountSlug || $[138] !== checkInDate || $[139] !== checkOutDate || $[140] !== minNumberOfBathrooms || $[141] !== minNumberOfBeds || $[142] !== minNumberOfRooms || $[143] !== propertyTypes) {
    t53 = routes.accountListingsSearchShow({
      slug: accountSlug,
      checkInDate: checkInDate || undefined,
      checkOutDate: checkOutDate || undefined,
      bathroomCount: minNumberOfBathrooms,
      bedroomCount: minNumberOfRooms,
      bedCount: minNumberOfBeds,
      activePropertyTypes: propertyTypes.map(_temp7).join(",")
    });
    $[137] = accountSlug;
    $[138] = checkInDate;
    $[139] = checkOutDate;
    $[140] = minNumberOfBathrooms;
    $[141] = minNumberOfBeds;
    $[142] = minNumberOfRooms;
    $[143] = propertyTypes;
    $[144] = t53;
  } else {
    t53 = $[144];
  }
  let t54;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <Icon name={IconNames.MAP} size="lg" className="text-primary-main" />;
    $[145] = t54;
  } else {
    t54 = $[145];
  }
  let t55;
  if ($[146] !== t53) {
    t55 = <div className="flex lg:hidden pr-2"><Link to={t53} aria-label="Go to Map View" variant="outlined" color="gray" size="sm">{t54}</Link></div>;
    $[146] = t53;
    $[147] = t55;
  } else {
    t55 = $[147];
  }
  let t56;
  if ($[148] !== t52 || $[149] !== t55) {
    t56 = <div className="pt-8 sm:pt-4 pl-2 w-full items-center justify-left align-middle flex">{t52}{t55}</div>;
    $[148] = t52;
    $[149] = t55;
    $[150] = t56;
  } else {
    t56 = $[150];
  }
  let t57;
  if ($[151] !== checkInDate || $[152] !== checkOutDate || $[153] !== clearDates || $[154] !== firstBookableCheckIn || $[155] !== hasFiltersApplied || $[156] !== hasNoResults || $[157] !== isFavorited || $[158] !== listingsLoading || $[159] !== listingsQueryResults || $[160] !== nextPage || $[161] !== prevPage || $[162] !== resetFilters || $[163] !== setFavorited) {
    t57 = <div className="flex flex-col">{listingsLoading ? <div className="mt-16 items-center text-center w-full"><Spinner size={SpinnerSize.SIZE_LARGE} /></div> : <>{listingsQueryResults?.listingsSearch.entries && listingsQueryResults?.listingsSearch.entries.length > 0 && <><div className="sm:mt-2 grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4 lg:gap-8">{listingsQueryResults.listingsSearch.entries.filter(_temp8).map(listing => <div key={listing.slug}><ListingCard listing={listing} altTag={`${listing.name || ""}`} data-testid={concatenateAndReplace(`${listing.name}`)} accountShowRoute={routes.accounts(listing.account.slug || "")} avatarUrl={listing.account.avatarUrl} showRoute={routes.listings({
                slug: listing.slug,
                checkInDate: checkInDate || undefined,
                checkOutDate: checkOutDate || undefined
              })} forceMobileView={true} isFavorited={isFavorited(listing.id)} onFavoritedSuccess={nowFavorited => setFavorited(listing.id, nowFavorited)} /></div>)}</div><div className="flex my-4 justify-center sm:mt-8 md:justify-end"><nav className="flex w-full text-center justify-center space-x-4"><Button variant="outlined" className={cx("border-[1.5px]", {
                "hover:cursor-not-allowed": !listingsQueryResults.listingsSearch.metadata.cursorBefore
              })} onClick={() => {
                prevPage();
                scrollToTop();
              }} disabled={!listingsQueryResults.listingsSearch.metadata.cursorBefore}>Previous</Button><Button variant="outlined" className={cx("border-[1.5px]", {
                "hover:cursor-not-allowed": !listingsQueryResults.listingsSearch.metadata.cursorAfter
              })} onClick={() => {
                nextPage();
                scrollToTop();
              }} disabled={!listingsQueryResults.listingsSearch.metadata.cursorAfter}>Next</Button></nav></div></>}{hasNoResults && <div className="pl-4 sm:pl-8 pt-8 sm:py-12 w-full text-primary-main">{firstBookableCheckIn ? <CheckInTooSoonNotice firstBookableOn={firstBookableCheckIn} className="text-primary-main" /> : <><div className="text-xl font-semibold">NO RESULTS</div><div className="text-lg font-medium pt-6">To get more results, try adjusting your search.</div></>}<div className="mt-6 items-center space-y-4 flex flex-col xs:flex-row xs:space-x-4 xs:space-y-0">{(checkInDate || checkOutDate) && <Button variant="outlined" onClick={clearDates}>Clear Dates</Button>}{hasFiltersApplied && <Button variant="outlined" onClick={resetFilters}>Reset Filters</Button>}</div></div>}</>}</div>;
    $[151] = checkInDate;
    $[152] = checkOutDate;
    $[153] = clearDates;
    $[154] = firstBookableCheckIn;
    $[155] = hasFiltersApplied;
    $[156] = hasNoResults;
    $[157] = isFavorited;
    $[158] = listingsLoading;
    $[159] = listingsQueryResults;
    $[160] = nextPage;
    $[161] = prevPage;
    $[162] = resetFilters;
    $[163] = setFavorited;
    $[164] = t57;
  } else {
    t57 = $[164];
  }
  let t58;
  if ($[165] !== t49 || $[166] !== t56 || $[167] !== t57) {
    t58 = <div className="relative">{t49}{t56}{t57}</div>;
    $[165] = t49;
    $[166] = t56;
    $[167] = t57;
    $[168] = t58;
  } else {
    t58 = $[168];
  }
  let t59;
  if ($[169] !== t58 || $[170] !== t6) {
    t59 = <>{t6}{t58}</>;
    $[169] = t58;
    $[170] = t6;
    $[171] = t59;
  } else {
    t59 = $[171];
  }
  return t59;
};
function _temp(val) {
  if (val > 0) {
    return val - 1;
  } else {
    return val;
  }
}
function _temp2(val_0) {
  return val_0 + 1;
}
function _temp3(val_1) {
  if (val_1 > 0) {
    return val_1 - 1;
  } else {
    return val_1;
  }
}
function _temp4(val_2) {
  return val_2 + 1;
}
function _temp5(val_3) {
  if (val_3 > 0) {
    return val_3 - 1;
  } else {
    return val_3;
  }
}
function _temp6(val_4) {
  return val_4 + 1;
}
function _temp7(propertyType) {
  return propertyType;
}
function _temp8(l) {
  return l.slug;
}